import {FC, useState, useEffect} from 'react'
import {useParams} from 'react-router-dom'
import {PageTitle} from '../../../../../_metronic/layout/core'
import {Button} from '../../../../components/Button/Button'
import {Input} from '../../../../components/Input/Input'
import {EditorQuill} from '../../../../components/EditorQuill/EditorQuill'
import {Loading} from '../../../../components/Loading/Loading'
import {GET, POST, DELETE, PUT} from '../../../../services/api'
import Swal from 'sweetalert2'
import {i18n} from '../../../../translate/i18n'
import {BreadCrumbs} from '../../../../components/BreadCrumbs/breadCrumbrs'
import SVG from 'react-inlinesvg'
import useModals from '../../../../hooks/useModals'
import uuid from 'react-uuid'
import styles from './cleanSafe.module.css'
import {IconPickerModal} from './components'

type typeSession = {
  SAFETY_MEASURES: 'safetyMeasures'
  CLEANING_PROTOCOLS: 'cleaningProtocols'
  CERTIFICATIONS: 'certifications'
}

interface SecurityMeasureItem {
  id: string
  backId: string
  icon: string
  iconId: number | null
  iconSvg: string
  title: string
  description: string
  status: 'active' | 'inactive'
  typeSession: typeSession['SAFETY_MEASURES']
}

interface CleaningProtocolItem {
  id: string
  backId: string
  title: string
  description: string
  status: 'active' | 'inactive'
  typeSession: typeSession['CLEANING_PROTOCOLS']
}

interface CertificationItem {
  id: string
  backId: string
  title: string
  status: 'active' | 'inactive'
  typeSession: typeSession['CERTIFICATIONS']
}
interface SecurityMeasureCardProps {
  item: SecurityMeasureItem
  index: number
  onChange: (id: string, field: keyof SecurityMeasureItem, value: any) => void
  onRemove: (id: string) => void
  onOpenIconPicker: (id: string) => void
  errors: {[key: string]: string}
}

const SecurityMeasureCard: FC<SecurityMeasureCardProps> = ({
  item,
  index,
  onChange,
  onRemove,
  onOpenIconPicker,
  errors,
}) => {
  return (
    <div className={`card mb-4 ${styles.card}`}>
      <div className={`card-header ${styles.cardHeader}`}>
        <h5 className='card-title'>
          {i18n.t('clean_safe.security_measure')} {index + 1}
        </h5>
        <div className='d-flex align-items-center'>
          <Button
            text={i18n.t('clean_safe.remove')}
            color='danger'
            size='small'
            icon='fas fa-trash'
            click={() => onRemove(item.id)}
          />
        </div>
      </div>
      <div className={`card-body ${styles.cardBody}`}>
        <div className='row'>
          <div className='col-md-6'>
            <label className='form-label required'>{i18n.t('clean_safe.icon')}</label>
            <div className='d-flex align-items-center gap-3'>
              <div
                className={`${styles.iconPreview} d-flex align-items-center justify-content-center border rounded`}
                onClick={() => onOpenIconPicker(item.id)}
              >
                {item.iconSvg ? (
                  <SVG src={item.iconSvg} style={{width: 24, height: 24}} />
                ) : (
                  <i className={`bi ${item.icon || 'bi-image'}`} />
                )}
              </div>
              <Button
                text={i18n.t('clean_safe.choose_icon')}
                color='light'
                size='small'
                click={() => onOpenIconPicker(item.id)}
              />
            </div>
            {errors[`${item.id}_icon`] && (
              <div className='text-danger small mt-1'>{errors[`${item.id}_icon`]}</div>
            )}
          </div>
          <div className='col-md-6'>
            <Input
              inputAttr={{type: 'select'}}
              label={i18n.t('clean_safe.status')}
              value={item.status}
              change={(value: 'active' | 'inactive') => onChange(item.id, 'status', value)}
              options={[
                {select: i18n.t('clean_safe.active'), value: 'active'},
                {select: i18n.t('clean_safe.inactive'), value: 'inactive'},
              ]}
              required={true}
              marginTop='0'
            />
          </div>
        </div>

        <div className='row mt-3'>
          <div className='col-md-12'>
            <Input
              inputAttr={{type: 'default'}}
              label={i18n.t('clean_safe.title_field')}
              value={item.title}
              required={true}
              change={(value: string) => onChange(item.id, 'title', value)}
              placeholder={i18n.t('clean_safe.placeholder_security_title')}
              marginTop='0'
              maxCaracteres={30}
            />
            {errors[`${item.id}_title`] && (
              <div className='text-danger small mt-1'>{errors[`${item.id}_title`]}</div>
            )}
          </div>
        </div>

        <div className='row mt-3'>
          <div className='col-md-12'>
            <label className='form-label'>{i18n.t('clean_safe.description')}</label>
            <div className={styles.quillContainer}>
              <EditorQuill
                value={item.description}
                change={(value: string) => onChange(item.id, 'description', value)}
                height={150}
                maxLength={100}
                placeholder={i18n.t('clean_safe.placeholder_security_description')}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

interface CleaningProtocolCardProps {
  item: CleaningProtocolItem
  index: number
  onChange: (id: string, field: keyof CleaningProtocolItem, value: any) => void
  onRemove: (id: string) => void
  errors: {[key: string]: string}
}

const CleaningProtocolCard: FC<CleaningProtocolCardProps> = ({
  item,
  index,
  onChange,
  onRemove,
  errors,
}) => {
  return (
    <div className={`card mb-4 ${styles.card}`}>
      <div className={`card-header ${styles.cardHeader}`}>
        <h5 className='card-title'>
          {i18n.t('clean_safe.cleaning_protocol')} {index + 1}
        </h5>
        <div className='d-flex align-items-center'>
          <Button
            text={i18n.t('clean_safe.remove')}
            color='danger'
            size='small'
            icon='fas fa-trash'
            click={() => onRemove(item.id)}
          />
        </div>
      </div>
      <div className={`card-body ${styles.cardBody}`}>
        <div className='row'>
          <div className='col-md-6'>
            <Input
              inputAttr={{type: 'default'}}
              label={i18n.t('clean_safe.title_field')}
              value={item.title}
              change={(value: string) => onChange(item.id, 'title', value)}
              placeholder={i18n.t('clean_safe.placeholder_protocol_title')}
              required={true}
              marginTop='0'
              maxCaracteres={30}
            />
            {errors[`${item.id}_title`] && (
              <div className='text-danger small mt-1'>{errors[`${item.id}_title`]}</div>
            )}
          </div>
          <div className='col-md-6'>
            <Input
              inputAttr={{type: 'select'}}
              label={i18n.t('clean_safe.status')}
              value={item.status}
              change={(value: 'active' | 'inactive') => onChange(item.id, 'status', value)}
              options={[
                {select: i18n.t('clean_safe.active'), value: 'active'},
                {select: i18n.t('clean_safe.inactive'), value: 'inactive'},
              ]}
              required={true}
              marginTop='0'
            />
          </div>
        </div>
        <div className='row mt-3'>
          <div className='col-md-12'>
            <label className='form-label'>{i18n.t('clean_safe.description')}</label>
            <EditorQuill
              value={item.description}
              change={(value: string) => onChange(item.id, 'description', value)}
              height={200}
              maxLength={300}
              placeholder={i18n.t('clean_safe.placeholder_protocol_description')}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

interface CertificationItemCardProps {
  item: CertificationItem
  index: number
  onChange: (id: string, field: keyof CertificationItem, value: any) => void
  onRemove: (id: string) => void
  errors: {[key: string]: string}
}

const CertificationItemCard: FC<CertificationItemCardProps> = ({
  item,
  index,
  onChange,
  onRemove,
  errors,
}) => {
  return (
    <div className={`card mb-4 ${styles.card}`}>
      <div className={`card-header ${styles.cardHeader}`}>
        <h5 className='card-title'>
          {i18n.t('clean_safe.certification')} {index + 1}
        </h5>
        <div className='d-flex align-items-center'>
          <Button
            text={i18n.t('clean_safe.remove')}
            color='danger'
            size='small'
            icon='fas fa-trash'
            click={() => onRemove(item.id)}
          />
        </div>
      </div>
      <div className={`card-body ${styles.cardBody}`}>
        <div className='row'>
          <div className='col-md-6'>
            <Input
              inputAttr={{type: 'default'}}
              label={i18n.t('clean_safe.title_field')}
              value={item.title}
              change={(value: string) => onChange(item.id, 'title', value)}
              placeholder={i18n.t('clean_safe.placeholder_certification_title')}
              marginTop='0'
              required={true}
              maxCaracteres={30}
            />
            {errors[`${item.id}_title`] && (
              <div className='text-danger small mt-1'>{errors[`${item.id}_title`]}</div>
            )}
          </div>
          <div className='col-md-6'>
            <Input
              inputAttr={{type: 'select'}}
              label={i18n.t('clean_safe.status')}
              value={item.status}
              change={(value: 'active' | 'inactive') => onChange(item.id, 'status', value)}
              options={[
                {select: i18n.t('clean_safe.active'), value: 'active'},
                {select: i18n.t('clean_safe.inactive'), value: 'inactive'},
              ]}
              marginTop='0'
              required={true}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

const addItem = <T extends object>(setList: React.Dispatch<React.SetStateAction<T[]>>, item: T) =>
  setList((prev) => [...prev, item])

const updateItem =
  <T extends {id: string}>(
    setList: React.Dispatch<React.SetStateAction<T[]>>,
    errors: {[k: string]: string},
    setErrors: React.Dispatch<React.SetStateAction<{[k: string]: string}>>,
    setTabErrors?: React.Dispatch<React.SetStateAction<{[k: string]: boolean}>>
  ) =>
  (id: string, field: keyof T, value: any) => {
    setList((prev) => prev.map((it) => (it.id === id ? ({...it, [field]: value} as T) : it)))
    const errorKey = `${id}_${String(field)}`
    if (errors[errorKey]) {
      const next = {...errors}
      delete next[errorKey]
      setErrors(next)

      if (setTabErrors) {
        setTabErrors((prev) => ({...prev, tab1: false, tab2: false, tab3: false}))
      }
    }
  }

const removeItemWithConfirm =
  <T extends {id: string; backId: string}>(
    setList: React.Dispatch<React.SetStateAction<T[]>>,
    list: T[],
    endpointPrefix: string,
    confirmText: {title: string; text: string}
  ) =>
  async (id: string) => {
    const item = list.find((it) => it.id === id)

    // Se o item não tem backId, remove diretamente (item novo não salvo no banco)
    if (!item?.backId) {
      const result = await Swal.fire({
        title: confirmText.title,
        text: confirmText.text,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: String(i18n.t('clean_safe.yes')),
        cancelButtonText: String(i18n.t('clean_safe.no')),
      })

      if (result.isConfirmed) {
        setList((prev) => prev.filter((it) => it.id !== id))
      }
      return
    }

    // Se tem backId, confirma antes de deletar do backend
    try {
      const result = await Swal.fire({
        title: confirmText.title,
        text: confirmText.text,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: String(i18n.t('clean_safe.yes')),
        cancelButtonText: String(i18n.t('clean_safe.no')),
      })

      if (result.isConfirmed) {
        await DELETE(`${endpointPrefix}/${item.backId}`)
        setList((prev) => prev.filter((it) => it.id !== id))
      }
    } catch (e) {
      console.error('Erro ao deletar item:', e)
      Swal.fire({
        icon: 'error',
        title: String(i18n.t('clean_safe.error')),
        text: String(i18n.t('clean_safe.error_delete')),
      })
    }
  }

const Content: FC = () => {
  const [loading, setLoading] = useState(false)
  const [activeTab, setActiveTab] = useState('#tab1')
  const {showWrapperModal} = useModals()

  const [securityMeasures, setSecurityMeasures] = useState<SecurityMeasureItem[]>([])
  const [cleaningProtocols, setCleaningProtocols] = useState<CleaningProtocolItem[]>([])
  const [certifications, setCertifications] = useState<CertificationItem[]>([])

  const [icons, setIcons] = useState<any[]>([])
  const [selectedIcon, setSelectedIcon] = useState<string>('')
  const [selectedIconId, setSelectedIconId] = useState<number | null>(null)
  const [currentEditingItemId, setCurrentEditingItemId] = useState<string | null>(null)

  const [errors, setErrors] = useState<{[key: string]: string}>({})
  const [tabErrors, setTabErrors] = useState<{[key: string]: boolean}>({
    tab1: false,
    tab2: false,
    tab3: false,
  })

  // Adicionar
  const addSecurityMeasure = () =>
    addItem(setSecurityMeasures, {
      id: uuid(),
      icon: '',
      backId: '',
      iconId: null,
      iconSvg: '',
      title: '',
      description: '',
      status: 'active',
      typeSession: 'safetyMeasures',
    } as SecurityMeasureItem)

  const addCleaningProtocol = () =>
    addItem(setCleaningProtocols, {
      id: uuid(),
      backId: '',
      title: '',
      description: '',
      status: 'active',
      typeSession: 'cleaningProtocols',
    } as CleaningProtocolItem)

  const addCertification = () =>
    addItem(setCertifications, {
      id: uuid(),
      backId: '',
      title: '',
      status: 'active',
      typeSession: 'certifications',
    } as CertificationItem)

  // Atualizar
  const updateSecurityMeasure = updateItem<SecurityMeasureItem>(
    setSecurityMeasures,
    errors,
    setErrors,
    setTabErrors
  )
  const updateCleaningProtocol = updateItem<CleaningProtocolItem>(
    setCleaningProtocols,
    errors,
    setErrors,
    setTabErrors
  )
  const updateCertification = updateItem<CertificationItem>(
    setCertifications,
    errors,
    setErrors,
    setTabErrors
  )

  // Remover
  const removeSecurityMeasure = removeItemWithConfirm<SecurityMeasureItem>(
    setSecurityMeasures,
    securityMeasures,
    '/clean-safe',
    {
      title: i18n.t('clean_safe.confirmation'),
      text: i18n.t('clean_safe.delete_security_measure_confirm'),
    }
  )

  const removeCleaningProtocol = removeItemWithConfirm<CleaningProtocolItem>(
    setCleaningProtocols,
    cleaningProtocols,
    '/clean-safe',
    {
      title: i18n.t('clean_safe.confirmation'),
      text: i18n.t('clean_safe.delete_cleaning_protocol_confirm'),
    }
  )

  const removeCertification = removeItemWithConfirm<CertificationItem>(
    setCertifications,
    certifications,
    '/clean-safe',
    {
      title: i18n.t('clean_safe.confirmation'),
      text: i18n.t('clean_safe.delete_certification_confirm'),
    }
  )

  // Modal ícones
  const openIconPicker = (itemId: string) => {
    const item = securityMeasures.find((sm) => sm.id === itemId)
    if (item) {
      setSelectedIcon(item.icon)
      setSelectedIconId(item.iconId)
      setCurrentEditingItemId(itemId)
      showWrapperModal(i18n.t('clean_safe.choose_icon'))
    }
  }

  const confirmIconSelection = () => {
    if (currentEditingItemId && selectedIconId !== null) {
      const chosenIcon = icons.find((i: any) => i.id === selectedIconId)
      updateSecurityMeasure(currentEditingItemId, 'iconId', selectedIconId)
      updateSecurityMeasure(currentEditingItemId, 'icon', selectedIcon)
      if (chosenIcon?.svg) {
        updateSecurityMeasure(currentEditingItemId, 'iconSvg', chosenIcon.svg)
      }
    }
    setCurrentEditingItemId(null)
    setSelectedIcon('')
    setSelectedIconId(null)
  }

  const ensureDescription = (html: string) => {
    const parser = new DOMParser()
    const doc = parser.parseFromString(html, 'text/html')
    const text = doc.body.textContent?.trim() || ''

    return text === '' ? '' : html.trim()
  }

  const validateForm = () => {
    const next: {[k: string]: string} = {}
    const nextTabErrors = {tab1: false, tab2: false, tab3: false}

    // Tab 1 - Security Measures
    securityMeasures.forEach((item) => {
      if (!item.title.trim()) {
        next[`${item.id}_title`] = i18n.t('clean_safe.required_title')
        nextTabErrors.tab1 = true
      }
      if (item.iconId === null || item.iconId === undefined) {
        next[`${item.id}_icon`] = i18n.t('clean_safe.required_icon')
        nextTabErrors.tab1 = true
      }
    })

    // Tab 2 - Cleaning Protocols
    cleaningProtocols.forEach((item) => {
      if (!item.title.trim()) {
        next[`${item.id}_title`] = i18n.t('clean_safe.required_title')
        nextTabErrors.tab2 = true
      }
    })

    // Tab 3 - Certifications
    certifications.forEach((item) => {
      if (!item.title.trim()) {
        next[`${item.id}_title`] = i18n.t('clean_safe.required_title')
        nextTabErrors.tab3 = true
      }
    })

    setErrors(next)
    setTabErrors(nextTabErrors)
    return Object.keys(next).length === 0
  }

  const handleSave = async () => {
    if (!validateForm()) {
      Swal.fire({
        icon: 'error',
        title: String(i18n.t('clean_safe.required_fields')),
        text: String(i18n.t('clean_safe.required_fields_message')),
      })
      return
    }

    try {
      setLoading(true)
      const itemsToCreate: any[] = []
      const itemsToUpdate: any[] = []

      const upsert = (hasBackId: boolean, payload: any, id?: string | number) => {
        if (hasBackId) itemsToUpdate.push({id, ...payload})
        else itemsToCreate.push(payload)
      }

      // Security
      securityMeasures.forEach((item) => {
        const payload = {
          iconId: item.iconId,
          title: item.title.trim(),
          description: ensureDescription(item.description),
          typeSession: 'safetyMeasures',
          status: item.status === 'active',
        }
        upsert(!!item.backId, payload, item.backId)
      })

      // Cleaning
      cleaningProtocols.forEach((item) => {
        const payload = {
          title: item.title.trim(),
          description: ensureDescription(item.description),
          typeSession: 'cleaningProtocols',
          status: item.status === 'active',
        }
        upsert(!!item.backId, payload, item.backId)
      })

      // Certifications
      certifications.forEach((item) => {
        const payload = {
          title: item.title.trim(),
          typeSession: 'certifications',
          status: item.status === 'active',
        }
        upsert(!!item.backId, payload, item.backId)
      })

      if (itemsToCreate.length > 0) await POST('/clean-safe/multiple', itemsToCreate)
      if (itemsToUpdate.length > 0) await PUT('/clean-safe/multiple', itemsToUpdate)

      await getAllContent()

      Swal.fire({
        icon: 'success',
        title: String(i18n.t('clean_safe.success')),
        text: String(i18n.t('clean_safe.success_save')),
      })
    } catch (error) {
      console.error('Erro ao salvar:', error)
      Swal.fire({
        icon: 'error',
        title: String(i18n.t('clean_safe.error')),
        text: String(i18n.t('clean_safe.error_save')),
      })
    } finally {
      setLoading(false)
    }
  }

  const loadIcons = async () => {
    try {
      const res = await POST('/svgwebapp/filter')
      setIcons(res?.data ?? [])
    } catch (error) {
      console.error('Erro ao carregar ícones:', error)
      setIcons([])
    }
  }

  const loadIconsSvg = async (securityItems: SecurityMeasureItem[]) => {
    try {
      const updated = securityItems.map((item) => {
        if (item.iconId) {
          const foundIcon = icons.find((icon: any) => icon.id === item.iconId)
          return {
            ...item,
            iconSvg: foundIcon?.svg || '',
            icon: foundIcon?.class || foundIcon?.value || foundIcon?.name || foundIcon?.slug || '',
          }
        }
        return item
      })
      setSecurityMeasures(updated)
    } catch (error) {
      console.error('Erro ao carregar SVGs dos ícones:', error)
    }
  }

  const getAllContent = async () => {
    try {
      setLoading(true)
      const res = await GET(`/clean-safe`)

      if (res?.data) {
        const data = res.data

        const securityData = data.filter((it: any) => it.typeSession === 'safetyMeasures')
        const cleaningData = data.filter((it: any) => it.typeSession === 'cleaningProtocols')
        const certificationData = data.filter((it: any) => it.typeSession === 'certifications')

        const mappedSecurity: SecurityMeasureItem[] = securityData.map((item: any) => ({
          id: item.id.toString(),
          backId: item.id,
          icon: '',
          title: item.title || '',
          description: item.description || '',
          iconId: item.iconId || null,
          iconSvg: '',
          typeSession: 'SAFETY_MEASURES',
          status: item.status ? 'active' : 'inactive',
        }))

        const mappedCleaning: CleaningProtocolItem[] = cleaningData.map((item: any) => ({
          id: item.id.toString(),
          backId: item.id,
          title: item.title || '',
          description: item.description || '',
          typeSession: 'CLEANING_PROTOCOLS',
          status: item.status ? 'active' : 'inactive',
        }))

        const mappedCertifications: CertificationItem[] = certificationData.map((item: any) => ({
          id: item.id.toString(),
          backId: item.id,
          title: item.title || '',
          typeSession: 'CERTIFICATIONS',
          status: item.status ? 'active' : 'inactive',
        }))

        setSecurityMeasures(mappedSecurity)
        setCleaningProtocols(mappedCleaning)
        setCertifications(mappedCertifications)

        if (mappedSecurity.length > 0 && icons.length > 0) {
          await loadIconsSvg(mappedSecurity)
        }
      }
    } catch (error) {
      console.error('Erro ao carregar conteúdo:', error)
    } finally {
      setLoading(false)
    }
  }

  const init = async () => {
    try {
      setLoading(true)
      await loadIcons()
    } catch (error) {
      console.error('Erro ao inicializar:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    init()
  }, [])

  useEffect(() => {
    if (icons.length > 0) getAllContent()
  }, [icons])

  useEffect(() => {
    if (icons.length > 0 && securityMeasures.length > 0) {
      const itemsWithIcons = securityMeasures.filter((it) => it.iconId)
      if (itemsWithIcons.length > 0) {
        loadIconsSvg(securityMeasures)
      }
    }
  }, [icons])

  // Função para determinar a classe da tab
  const getTabClass = (tabId: string, isActive: boolean) => {
    const baseClass = styles.tabItem

    if (tabErrors[tabId]) {
      return `${baseClass} ${styles.tabError} ${isActive ? 'active' : ''}`
    }

    return `${baseClass} ${isActive ? styles.tabActive : styles.tabInactive}`
  }

  const iconClassOf = (it: any): string =>
    it?.class ?? it?.value ?? it?.name ?? it?.slug ?? it?.id ?? ''

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <div className='row g-5 gx-xxl-12'>
          <div className='card card-xxl-stretch mb-5 mb-xl-12'>
            <div className='card-header py-5'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bolder fs-3 mb-1'>{i18n.t('clean_safe.title')}</span>
                <BreadCrumbs />
                <span className='text-muted mt-1 fw-bold fs-7'>
                  {i18n.t('clean_safe.subtitle')}
                </span>
              </h3>
            </div>

            {/* Tabs */}
            <div className='card-body'>
              <div className='d-flex justify-content-between'>
                <div className={`d-flex ${styles.tabMenu}`}>
                  <div
                    className={getTabClass('tab1', activeTab === '#tab1')}
                    onClick={() => setActiveTab('#tab1')}
                  >
                    <i className='fas fa-shield-alt me-2'></i>
                    {i18n.t('clean_safe.security_measures')}
                  </div>
                  <div
                    className={getTabClass('tab2', activeTab === '#tab2')}
                    onClick={() => setActiveTab('#tab2')}
                  >
                    <i className='fas fa-broom me-2'></i>
                    {i18n.t('clean_safe.cleaning_protocols')}
                  </div>
                  <div
                    className={getTabClass('tab3', activeTab === '#tab3')}
                    onClick={() => setActiveTab('#tab3')}
                  >
                    <i className='fas fa-certificate me-2'></i>
                    {i18n.t('clean_safe.certifications')}
                  </div>
                </div>
              </div>

              {/* Conteúdo Tabs */}
              <div className='mt-5'>
                {activeTab === '#tab1' && (
                  <div>
                    <div className='d-flex justify-content-between align-items-center mb-4'>
                      <h4>{i18n.t('clean_safe.security_measures')}</h4>
                      <Button
                        text={i18n.t('clean_safe.add_security_measure')}
                        color='primary'
                        size='small'
                        icon='fas fa-plus'
                        click={addSecurityMeasure}
                      />
                    </div>
                    {errors['general_securityMeasures'] && (
                      <div className='alert alert-warning'>
                        {errors['general_securityMeasures']}
                      </div>
                    )}
                    {securityMeasures.length === 0 ? (
                      <div className={`${styles.emptyState}`}>
                        <i className='fas fa-shield-alt fa-3x'></i>
                        <p>{i18n.t('clean_safe.empty_security_measures')}</p>
                        <p>{i18n.t('clean_safe.empty_start_message')}</p>
                      </div>
                    ) : (
                      <>
                        {securityMeasures.map((item, index) => (
                          <SecurityMeasureCard
                            key={item.id}
                            item={item}
                            index={index}
                            onChange={updateSecurityMeasure}
                            onRemove={removeSecurityMeasure}
                            onOpenIconPicker={openIconPicker}
                            errors={errors}
                          />
                        ))}
                        <div className='text-center mt-4'>
                          <Button
                            text={i18n.t('clean_safe.add_security_measure')}
                            color='success'
                            size='small'
                            icon='fas fa-plus'
                            click={addSecurityMeasure}
                            btnClass='w-100'
                          />
                        </div>
                      </>
                    )}
                  </div>
                )}

                {activeTab === '#tab2' && (
                  <div>
                    <div className='d-flex justify-content-between align-items-center mb-4'>
                      <h4>{i18n.t('clean_safe.cleaning_protocols')}</h4>
                      <Button
                        text={i18n.t('clean_safe.add_cleaning_protocol')}
                        color='primary'
                        size='small'
                        icon='fas fa-plus'
                        click={addCleaningProtocol}
                      />
                    </div>
                    {errors['general_cleaningProtocols'] && (
                      <div className='alert alert-warning'>
                        {errors['general_cleaningProtocols']}
                      </div>
                    )}
                    {cleaningProtocols.length === 0 ? (
                      <div className={`${styles.emptyState}`}>
                        <i className='fas fa-broom fa-3x'></i>
                        <p>{i18n.t('clean_safe.empty_cleaning_protocols')}</p>
                        <p>{i18n.t('clean_safe.empty_start_message')}</p>
                      </div>
                    ) : (
                      <>
                        {cleaningProtocols.map((item, index) => (
                          <CleaningProtocolCard
                            key={item.id}
                            item={item}
                            index={index}
                            onChange={updateCleaningProtocol}
                            onRemove={removeCleaningProtocol}
                            errors={errors}
                          />
                        ))}
                        <div className='text-center mt-4'>
                          <Button
                            text={i18n.t('clean_safe.add_cleaning_protocol')}
                            color='success'
                            size='small'
                            icon='fas fa-plus'
                            click={addCleaningProtocol}
                            btnClass='w-100'
                          />
                        </div>
                      </>
                    )}
                  </div>
                )}

                {activeTab === '#tab3' && (
                  <div>
                    <div className='d-flex justify-content-between align-items-center mb-4'>
                      <h4>{i18n.t('clean_safe.certifications')}</h4>
                      <Button
                        text={i18n.t('clean_safe.add_certification')}
                        color='primary'
                        size='small'
                        icon='fas fa-plus'
                        click={addCertification}
                      />
                    </div>
                    {errors['general_certifications'] && (
                      <div className='alert alert-warning'>{errors['general_certifications']}</div>
                    )}
                    {certifications.length === 0 ? (
                      <div className={`${styles.emptyState}`}>
                        <i className='fas fa-certificate fa-3x'></i>
                        <p>{i18n.t('clean_safe.empty_certifications')}</p>
                        <p>{i18n.t('clean_safe.empty_start_message')}</p>
                      </div>
                    ) : (
                      <>
                        {certifications.map((item, index) => (
                          <CertificationItemCard
                            key={item.id}
                            item={item}
                            index={index}
                            onChange={updateCertification}
                            onRemove={removeCertification}
                            errors={errors}
                          />
                        ))}
                        <div className='text-center mt-4'>
                          <Button
                            text={i18n.t('clean_safe.add_certification')}
                            color='success'
                            size='small'
                            icon='fas fa-plus'
                            click={addCertification}
                            btnClass='w-100'
                          />
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Ações */}
            <div className='card-footer border-0 pt-0 d-flex justify-content-end'>
              <Button
                text={i18n.t('clean_safe.save')}
                color='primary'
                size='small'
                horizontalPadding={6}
                btnAttr={{type: 'button'}}
                click={handleSave}
                disabled={loading}
                btnClass='fw-bolder'
              />
            </div>
          </div>

          {/* Modal Ícones */}
          <IconPickerModal
            isOpen={false}
            icons={icons}
            selectedIconId={selectedIconId}
            selectedIcon={selectedIcon}
            setSelectedIcon={setSelectedIcon}
            setSelectedIconId={setSelectedIconId}
            onClose={() => {
              setCurrentEditingItemId(null)
              setSelectedIcon('')
              setSelectedIconId(null)
            }}
            onConfirm={confirmIconSelection}
            wrapperModalOpen={true}
          />
        </div>
      )}
    </>
  )
}

const CleanSafe: FC = () => {
  return (
    <>
      <PageTitle
        breadcrumbs={[
          {title: 'Dashboard', path: 'dashboard', isActive: false},
          {title: i18n.t('menu.hotel_maintenance'), path: 'hospitality', isActive: false},
          {title: i18n.t('clean_safe.title'), path: 'clean-safe', isActive: true},
        ]}
      >
        {i18n.t('clean_safe.title')}
      </PageTitle>
      <Content />
    </>
  )
}

export {CleanSafe}
